import React, { useCallback, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { getAccessToken, getRefreshToken } from '../../storage/auth'
import { ReturnCard } from './return-card'
import { ErrorReference } from '../error/error-reference'

// A sign-in link (the token in /open/organization?token=…, /open/complete,
// /open/auth, /open/register, /open/link) that is already spent (owner
// report 2026-10-13). The auth service answers every /auth-provider/:token/*
// call for it with {"message", "reason"}:
//
//   sign_in_link_used (404)    -- the sign-in finished (an EducationXR app
//                                 took it, or this browser did);
//   sign_in_link_expired (410) -- it ran out of its 15 minutes unused, or
//                                 its sign-in ended without signing in
//                                 (a refused sign-in: auth ends the token).
//
// Neither is a fault: a tab the app's sign-in left behind (Back, a restored
// tab, Chrome on Android coming back through the identity provider) lands
// here after the app is already signed in. So no red alert and no support
// reference: with a session in this browser, straight into the admin;
// otherwise a calm card that says what happened.

export const SIGN_IN_LINK_USED = 'sign_in_link_used'
export const SIGN_IN_LINK_EXPIRED = 'sign_in_link_expired'

// The spent reason a failed call carries, or null (any other failure,
// including a 5xx or a network error, which keep their own error state).
export const spentReasonOf = error => {
  const reason = error && error.data && error.data.reason
  return reason === SIGN_IN_LINK_USED || reason === SIGN_IN_LINK_EXPIRED ? reason : null
}

// Whether this browser already holds a session (storage can throw).
export const hasSession = () => {
  try {
    return Boolean(getAccessToken() && getRefreshToken())
  } catch {
    return false
  }
}

// [spent, onSpent]: onSpent(error) is true when the error was a spent link
// and has been dealt with -- into the app (replace: the spent page leaves
// history) when this browser has a session, else spent is set for the
// page to render <SignInLinkSpent reason={spent} />.
export const useSignInLinkSpent = () => {
  const history = useHistory()
  const [spent, updateSpent] = useState(null)

  const onSpent = useCallback(
    error => {
      const reason = spentReasonOf(error)

      if (!reason) return false

      if (hasSession()) {
        history.replace('/')
      } else {
        updateSpent(reason)
      }

      return true
    },
    [history]
  )

  return [spent, onSpent]
}

// The link's first call failed for any other reason (a token auth doesn't
// know, a 5xx, a network error): say so, with the request's support
// reference when it has one -- never a blank page or "Login successful"
// (owner 2026-10-17). The outcome card's error variant (return-card.js).
export const SignInLinkFailed = ({ error }) => (
  <ReturnCard
    variant="error"
    id="link-failed-title"
    title="This sign-in link didn't work"
    lede="Something went wrong with this sign-in link. If you were signing in to an EducationXR app, start signing in again from the app."
    action={<Link replace className="btn sw-btn-primary sa-block" to="/auth/email">Sign in</Link>}>
    <ErrorReference error={error} />
  </ReturnCard>
)

// Used: the app has its sign-in, so this is the signed-in card with a way to
// sign in here too. Expired: the expired variant and "Start signing in
// again". Neither is an alert.
export const SignInLinkSpent = ({ reason }) => {
  const expired = reason === SIGN_IN_LINK_EXPIRED

  return expired
    ? <ReturnCard
        variant="expired"
        id="link-spent-title"
        title="This sign-in link has expired"
        lede="Sign-in links last 15 minutes, and stop working once their sign-in has ended. Start signing in again: if you were signing in to an EducationXR app, start from the app."
        action={<Link replace className="btn sw-btn-primary sa-block" to="/auth/email">Start signing in again</Link>} />
    : <ReturnCard
        variant="used"
        id="link-spent-title"
        title="This sign-in link has already been used"
        lede="If you were signing in to an EducationXR app, the app is signed in."
        action={<Link replace className="btn sw-btn sa-block" to="/auth/email">Sign in</Link>} />
}
